import { Component, Input, OnInit, inject } from '@angular/core';
import {
  SharedContact,
  SharedRecipient,
  SharedRecipientDetails,
} from '@compumark/order-management-client';

import { UserService } from '../../../../../security/services/user.service';
import { formatUtcToLocal } from '../../../../../util/date-util';

@Component({
  selector: 'app-shared-recipient-tool-tip',
  templateUrl: './shared-recipient-tool-tip.component.html',
  styleUrls: ['./shared-recipient-tool-tip.component.scss'],
  standalone: false,
})
export class SharedRecipientToolTipComponent implements OnInit {
  private static readonly LABEL_YOU = 'you';
  private static readonly EMPTY_STRING = '';
  private static readonly TOOLTIP_MAX_HEIGHT_PX = 412;
  private static readonly MEMBER_ROW_HEIGHT_PX = 20;
  private static readonly CONTACT_ROW_HEIGHT_PX = 24;

  private userService = inject(UserService);

  @Input() sharedRecipientDetails!: SharedRecipientDetails[];

  protected showRecipientDetails!: boolean;
  protected sharedByName!: string;
  protected dateShared!: string;

  protected hasOverflow!: boolean;
  protected displayedMembers!: Array<SharedRecipient>;
  protected membersCount!: number;
  protected contactsCount!: number;

  private firstShare?: SharedRecipientDetails;

  ngOnInit(): void {
    this.validateInput();
    if (!this.showRecipientDetails) {
      return;
    }
    this.firstShare = this.getFirstShare(this.sharedRecipientDetails);
    this.determineShareByNameAndDateShared();
    this.calculateVisibleMembers();
  }

  private validateInput(): void {
    const list = this.sharedRecipientDetails;

    this.showRecipientDetails =
      Array.isArray(list) &&
      list.length > 0 &&
      list.some(
        (details) =>
          !!details?.sharedBy &&
          !!details?.dateShared &&
          (details?.sharedTo?.length ?? 0) > 0,
      );
  }

  private getFirstShare(
    list: SharedRecipientDetails[],
  ): SharedRecipientDetails | undefined {
    return list?.reduce((oldest, current) => {
      return this.parseDate(current.dateShared) <
        this.parseDate(oldest.dateShared)
        ? current
        : oldest;
    });
  }

  private parseDate(dateStr?: string): Date {
    if (!dateStr) {
      return new Date(0);
    }
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? new Date(0) : date;
  }

  private determineShareByNameAndDateShared(): void {
    const sharedBy = this.firstShare?.sharedBy;
    this.sharedByName =
      (this.userService.getCurrentUser()?.contactId === sharedBy?.contactId
        ? SharedRecipientToolTipComponent.LABEL_YOU
        : sharedBy?.contactName) ??
      SharedRecipientToolTipComponent.EMPTY_STRING;
    this.dateShared =
      formatUtcToLocal(this.firstShare?.dateShared) ||
      SharedRecipientToolTipComponent.EMPTY_STRING;
  }

  private calculateVisibleMembers(): void {
    let accumulatedHeight = 0;
    this.displayedMembers = new Array<SharedRecipient>();
    this.hasOverflow = false;
    const allMembers =
      this.sharedRecipientDetails?.flatMap(
        (details) => details.sharedTo ?? [],
      ) ?? [];

    allMembers.forEach((sharedRecipient: SharedRecipient): void => {
      accumulatedHeight += this.calculateMemberRowHeight(sharedRecipient);

      if (
        accumulatedHeight >
        SharedRecipientToolTipComponent.TOOLTIP_MAX_HEIGHT_PX
      ) {
        this.hasOverflow = true;
        return;
      } else {
        this.displayedMembers.push(sharedRecipient);
      }
    });

    if (this.hasOverflow) {
      this.membersCount = allMembers.length;
      this.contactsCount = allMembers.flatMap(
        (member) => member.contacts ?? [],
      ).length;
    }
  }

  private calculateMemberRowHeight(sharedRecipient: SharedRecipient): number {
    const totalContacts = sharedRecipient.contacts?.length ?? 0;
    const visibleContacts = Math.min(totalContacts, 3);
    return (
      SharedRecipientToolTipComponent.MEMBER_ROW_HEIGHT_PX +
      visibleContacts * SharedRecipientToolTipComponent.CONTACT_ROW_HEIGHT_PX
    );
  }

  protected getVisibleContacts(
    contacts: Array<SharedContact> = [],
  ): SharedContact[] {
    return contacts.length > 3 ? contacts.slice(0, 2) : contacts;
  }

  protected getExtraContactsCount(contacts: Array<SharedContact> = []): number {
    return contacts.length > 3 ? contacts.length - 2 : 0;
  }
}
